import type {
  RestaurantConfigHttpEnvelope,
  RestaurantConfiguration,
  RestaurantDetailsWireDto,
} from "@/domain/restaurant/restaurant.types";

function isRecord(v: unknown): v is Record<string, unknown> {
  return v != null && typeof v === "object" && !Array.isArray(v);
}

/** Preserve API null; treat missing / non-string / whitespace-only as null. Never "". */
function normalizeOptionalString(value: unknown): string | null {
  if (typeof value !== "string") return null;
  const trimmed = value.trim();
  return trimmed.length > 0 ? trimmed : null;
}

/** Finite WGS84 coordinate, or null. Strings are not coerced. */
function normalizeCoordinate(value: unknown, min: number, max: number): number | null {
  if (typeof value !== "number" || !Number.isFinite(value)) return null;
  if (value < min || value > max) return null;
  return value;
}

export function unwrapRestaurantConfigEnvelope(
  envelope: RestaurantConfigHttpEnvelope | undefined
): RestaurantDetailsWireDto {
  if (!envelope?.success || envelope.resultCode !== 200 || !envelope.data) {
    throw new Error(envelope?.message || "Restaurant configuration failed to load");
  }
  return envelope.data;
}

export function mapRestaurantDetailsToConfiguration(
  dto: RestaurantDetailsWireDto,
  requestedRestaurantId: string
): RestaurantConfiguration {
  const restaurant_id = String(dto.restaurant_id ?? requestedRestaurantId).trim();
  if (!restaurant_id) {
    throw new Error("Restaurant configuration is missing restaurant_id");
  }

  return {
    restaurant_id,
    name: String(dto.name ?? "").trim(),
    brand_name: String(dto.brand_name ?? "").trim(),
    currency_code: String(dto.currency_code ?? "").trim(),
    currency_symbol: String(dto.currency_symbol ?? "").trim(),
    country_iso2: String(dto.country_iso2 ?? "").trim().toUpperCase(),
    timezone: String(dto.timezone ?? "").trim(),
    require_allergen_consent: Boolean(dto.require_allergen_consent),
    allergen_policy_pdf_url: normalizeOptionalString(dto.allergen_policy_pdf_url),
    logo_url: normalizeOptionalString(dto.logo_url),
    favicon_url: normalizeOptionalString(dto.favicon_url),
    theme_color: normalizeOptionalString(dto.theme_color),
    primary_button_color: normalizeOptionalString(dto.primary_button_color),
    secondary_button_color: normalizeOptionalString(dto.secondary_button_color),
    facebook_url: normalizeOptionalString(dto.facebook_url),
    instagram_url: normalizeOptionalString(dto.instagram_url),
    address: normalizeOptionalString(dto.address),
    city: normalizeOptionalString(dto.city),
    state: normalizeOptionalString(dto.state),
    zip_code: normalizeOptionalString(dto.zip_code),
    country: normalizeOptionalString(dto.country),
    latitude: normalizeCoordinate(dto.latitude, -90, 90),
    longitude: normalizeCoordinate(dto.longitude, -180, 180),
    contact_no: normalizeOptionalString(dto.contact_no),
    email: normalizeOptionalString(dto.email),
    website_pickup_enabled: dto.website_pickup_enabled === true,
  };
}

export function parseRestaurantConfigurationResponse(
  body: unknown,
  requestedRestaurantId: string
): RestaurantConfiguration {
  if (!isRecord(body)) {
    throw new Error("Invalid restaurant configuration response");
  }

  const dto = unwrapRestaurantConfigEnvelope(body as RestaurantConfigHttpEnvelope);
  return mapRestaurantDetailsToConfiguration(dto, requestedRestaurantId);
}
